import { useState, useEffect, useMemo } from "react";
import { userLsKey, clearUserScopedStorage } from "@/lib/userScopedStorage";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { 
  Target, 
  Menu, 
  Plus, 
  LogOut, 
  Home, 
  CheckSquare, 
  ListTodo,
  Settings,
  GripVertical,
  StickyNote,
  Shield,
  Tag,
  ChevronDown,
  ChevronRight,
  Film,
  Folder,
  HardDrive,
  CreditCard,
  Building2,
  UserCog,
  Contact,
  Bot,
  CalendarPlus,
  Zap,
  Award,
  Layers,
  Eye,
  ShieldCheck,
  Trophy,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import { useNavigationOrder, type NavigationItem } from "@/hooks/useNavigationOrder";
import { usePlanContext } from "@/contexts/PlanContext";
import { useLocation, Link } from "wouter";
import { menuItemDefinitions, getMenuItemsByParent, menuItemFeatureKeyOverrides, menuItemCapabilityMap, assistantCanAccessMenuItem, type AssistantCapability } from "@/lib/menu-items";
import type { ResellerConfig, CategoryStats, Category } from "@shared/schema";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import TourLauncher from "@/components/tour-launcher";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import NotificationBell from "@/components/notification-bell";
import { TimeClockButton } from "@/components/time-clock-button";
import { MessageBadge } from "@/components/messaging/message-badge";
import CreateGoalModal from "@/components/create-goal-modal";
import CreateHabitModal from "@/components/create-habit-modal";
import { CalendarSidebarPanel } from "@/components/sidebar";
import { ScorecardDialog } from "@/components/scorecard-dialog";

interface MobileHeaderProps {
  onCreateGoal?: () => void;
}

interface SortableNavItemProps {
  item: NavigationItem;
  onNavigate?: () => void;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
  isNestTarget?: boolean;
}

function SortableNavItem({ item, onNavigate, isOpen, onToggle, isNestTarget }: SortableNavItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = item.icon;
  const depth = item.depth || 0;
  const indentPadding = depth * 16;

  if (item.isCollapsible) {
    return (
      <div ref={setNodeRef} style={{ ...style, paddingLeft: indentPadding }} className={`relative group ${isNestTarget ? 'bg-primary/20 rounded-md' : ''}`}>
        <Collapsible open={isOpen} onOpenChange={onToggle}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className={`w-full justify-between ${
                item.current 
                  ? "text-primary" 
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
              data-testid={`button-mobile-${item.name.toLowerCase().replace(/\s+/g, '-')}-toggle`}
            >
              <span className="flex items-center">
                <Icon className="w-4 h-4 mr-3" />
                {item.name}
              </span>
              <span className="ml-auto">
                {isOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </span>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-1 mt-1">
            {item.children}
          </CollapsibleContent>
        </Collapsible>
        <button
          {...attributes}
          {...listeners}
          className="absolute left-0 top-1/2 -translate-y-1/2 p-1 opacity-60 cursor-grab active:cursor-grabbing"
          aria-label={`Drag to reorder ${item.name}`}
          data-testid={`drag-handle-mobile-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    );
  }

  return (
    <div 
      ref={setNodeRef} 
      style={{ ...style, paddingLeft: indentPadding }} 
      className={`group flex items-center gap-1 ${isNestTarget ? 'bg-primary/20 rounded-md' : ''}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="flex-shrink-0 p-1 opacity-60 cursor-grab active:cursor-grabbing"
        aria-label={`Drag to reorder ${item.name}`}
        data-testid={`drag-handle-mobile-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </button>
      <Link href={item.href} className="flex-grow min-w-0">
        <Button 
          variant="ghost" 
          className="w-full justify-start"
          onClick={onNavigate}
        >
          <Icon className="w-4 h-4 mr-3" />
          {item.name}
        </Button>
      </Link>
      {item.showTourLauncher && (
        <span className="flex-shrink-0">
          <TourLauncher variant="inline" />
        </span>
      )}
    </div>
  );
}

export default function MobileHeader({ onCreateGoal }: MobileHeaderProps) {
  const { user, tenant, isAdmin, isMasterAdmin, isAuthenticated } = useAuth();
  const { isPlatformOwner, hasPermission } = usePlatformPermissions();
  const [location] = useLocation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [nestTargetId, setNestTargetId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | 'nest' | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(true);
  
  // Scorecard dialog state
  const [scorecardOpen, setScorecardOpen] = useState(false);

  // Quick create menu state
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [createGoalOpen, setCreateGoalOpen] = useState(false);
  const [createHabitOpen, setCreateHabitOpen] = useState(false);
  // Initialize collapsible states from user-scoped localStorage (matching desktop sidebar)
  const [mindOpen, setMindOpen] = useState(true);
  const [achieveOpen, setAchieveOpen] = useState(true);
  const [organizeOpen, setOrganizeOpen] = useState(true);
  const [aiCoachOpen, setAiCoachOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);

  useEffect(() => {
    const uid = user?.id ?? null;
    const read = (key: string, def: boolean) => {
      try {
        const saved = localStorage.getItem(userLsKey(key, uid));
        if (saved !== null) return saved === 'true';
      } catch {}
      return def;
    };
    setMindOpen(read('sidebar-mind-open', true));
    setAchieveOpen(read('sidebar-achieve-open', true));
    setOrganizeOpen(read('sidebar-organize-open', true));
    setAiCoachOpen(read('sidebar-aicoach-open', false));
    setReviewOpen(read('sidebar-review-open', true));
    setSettingsOpen(read('sidebar-settings-open', false));
    setMediaOpen(read('sidebar-media-open', false));
    setCrmOpen(read('sidebar-crm-open', false));
    if (uid) {
      ['sidebar-mind-open','sidebar-achieve-open','sidebar-organize-open',
       'sidebar-aicoach-open','sidebar-review-open','sidebar-settings-open',
       'sidebar-media-open','sidebar-crm-open'].forEach(k => {
        try { localStorage.removeItem(k); } catch {}
      });
    }
  }, [user?.id]);
  
  // Persist collapsible states to user-scoped localStorage
  const mUid = user?.id ?? null;
  const handleMindToggle = (open: boolean) => {
    setMindOpen(open);
    localStorage.setItem(userLsKey('sidebar-mind-open', mUid), String(open));
  };
  const handleAchieveToggle = (open: boolean) => {
    setAchieveOpen(open);
    localStorage.setItem(userLsKey('sidebar-achieve-open', mUid), String(open));
  };
  const handleOrganizeToggle = (open: boolean) => {
    setOrganizeOpen(open);
    localStorage.setItem(userLsKey('sidebar-organize-open', mUid), String(open));
  };
  const handleAiCoachToggle = (open: boolean) => {
    setAiCoachOpen(open);
    localStorage.setItem(userLsKey('sidebar-aicoach-open', mUid), String(open));
  };
  const handleReviewToggle = (open: boolean) => {
    setReviewOpen(open);
    localStorage.setItem(userLsKey('sidebar-review-open', mUid), String(open));
  };
  const handleSettingsToggle = (open: boolean) => {
    setSettingsOpen(open);
    localStorage.setItem(userLsKey('sidebar-settings-open', mUid), String(open));
  };
  const handleMediaToggle = (open: boolean) => {
    setMediaOpen(open);
    localStorage.setItem(userLsKey('sidebar-media-open', mUid), String(open));
  };
  const handleCrmToggle = (open: boolean) => {
    setCrmOpen(open);
    localStorage.setItem(userLsKey('sidebar-crm-open', mUid), String(open));
  };
  const { hasFeature, isLoading: planLoading } = usePlanContext();

  const { data: resellerConfig } = useQuery<ResellerConfig>({
    queryKey: ["/api/reseller/config"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
  });

  // Check if current user is an assistant (to hide Assistants nav from assistants)
  const { data: assistantProfile } = useQuery<{ id: string } | null>({
    queryKey: ["/api/assistants/profile"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
  });
  const isAssistant = !!assistantProfile;
  const { data: effectivePermissions } = useQuery<{ permissions: Partial<Record<AssistantCapability, boolean>> } | null>({
    queryKey: ["/api/assistants/effective-permissions"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated && isAssistant,
  });

  // Set of nav item IDs an assistant can access, plus parent sections that have
  // at least one accessible child. null when the user is not an assistant.
  const assistantEnabledIds = useMemo<Set<string> | null>(() => {
    if (!isAssistant) return null;
    // While permissions are still loading (effectivePermissions is undefined),
    // fail closed — only dashboard is universally accessible until we know
    // which capabilities the manager has granted.
    if (effectivePermissions === undefined) {
      return new Set(["dashboard"]);
    }
    const perms = effectivePermissions?.permissions ?? {};
    const enabled = new Set<string>();
    // Dashboard is universally accessible — no capability required.
    enabled.add("dashboard");
    // Build a parent-id lookup for propagating access up to section headers.
    const parentOf: Record<string, string | undefined> = {};
    menuItemDefinitions.forEach(d => { if (d.parentId) parentOf[d.id] = d.parentId; });
    // Enable leaf items that pass assistantCanAccessMenuItem and propagate
    // that access up to their parent section(s) so the section header turns
    // green too.  Unmapped items (Communicate, Affirmations, etc.) stay gray.
    menuItemDefinitions.forEach(d => {
      if (assistantCanAccessMenuItem(d.id, perms)) {
        enabled.add(d.id);
        // Also enable every ancestor section so the hierarchy stays consistent.
        let pid = parentOf[d.id];
        while (pid) { enabled.add(pid); pid = parentOf[pid]; }
      }
    });
    return enabled;
  }, [isAssistant, effectivePermissions]);

  const { data: userPreferences } = useQuery<{ hiddenMenuItems?: string[] }>({
    queryKey: ["/api/user/preferences"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
  });
  const hiddenMenuItems: string[] = (userPreferences?.hiddenMenuItems as string[]) || [];

  const { data: categoryStats = {} } = useQuery<CategoryStats>({
    queryKey: ["/api/stats/categories"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
  });

  const { data: customCategories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
  });

  // Map navigation IDs to feature registry keys
  const featureKeyMap: Record<string, string> = {
    "dashboard": "dashboard",
    "my-goals": "goals",
    "habits": "habits",
    "tasks": "tasks",
    "planner": "planner",
    "journal": "journal",
    "notepad": "notepad",
    "intentions": "intentions",
    "gratefuls": "gratefuls",
    "checklists": "checklists",
    "media": "media",
    "drive": "drive",
    "community": "community",
    "calendar": "calendar",
    "meditation": "meditation",
    "analytics": "analytics",
    "achievements": "achievements",
    "tours": "tours",
    "assistants": "assistants",
    "media-library": "media",
    "google-drive": "drive",
    "zeus": "zeus",
    "vboard": "vboard",
    "trackerx": "trackerx",
    "health": "health",
    "meal-planner": "meal_planner",
    "workout-planner": "workout_planner",
    "health-metrics": "health_metrics",
    "food-goal": "food_goal",
    "fitness-goal": "fitness_goal",
    "ai-coach-start": "ai_coach",
    "ai-coach-ask": "ask_ai",
    "goal-simulation": "goal_simulation",
    "ai-create": "ai_create",
    "time-budget": "time_budget",
    "settings-main": "preferences",
    "sops": "sops",
    "zeus-settings": "zeus_settings",
    "vault": "vault",
    "billing": "billing",
    "api-keys": "api_keys",
    "suggestions": "suggestions",
    "testimonials": "testimonials",
    "contacts": "contacts",
    ...menuItemFeatureKeyOverrides,
  };

  // Navigation order matching desktop: Dashboard > Mind > Achieve > Organize > AI Coach > Review > Assistants
  const defaultNavigation = [
    // 1. Dashboard (top-level)
    { id: "dashboard", name: "Dashboard", href: "/", icon: Home, current: location === "/" },

    // 1b. Zeus (top-level) - master AI agent hub
    { id: "zeus", name: "Zeus", href: "/zeus", icon: Zap, current: location === "/zeus" },

    // 2. Mind section (collapsible) - Affirmations, Journal, Gratefuls, MnM
    { id: "mind", name: "Mind", href: "#", icon: Zap, current: false, isCollapsible: true },
    
    // 3. Achieve section (collapsible) - Habits, Goals, Tasks, Community
    { id: "achieve", name: "Achieve", href: "#", icon: Award, current: false, isCollapsible: true },
    
    // 4. Organize section (collapsible) - Calendar, Notepad, Planner, Checklists
    { id: "organize", name: "Organize", href: "#", icon: Layers, current: false, isCollapsible: true },
    
    // 5. AI Coach section (collapsible) - Coach, Ask AI, Goal Simulation, AI Create, Time Budget
    { id: "ai-coach", name: "AI Coach", href: "#", icon: Bot, current: location.startsWith("/ai-coach") || location === "/goal-simulation" || location === "/ai-create" || location === "/time-budget", isCollapsible: true },
    
    // 6. Media section (collapsible) - Media Library, Google Drive
    { id: "media", name: "Media", href: "#", icon: Film, current: false, isCollapsible: true },
    
    // 7. Review section (collapsible) - Analytics, Achievements, Settings
    { id: "review", name: "Review", href: "#", icon: Eye, current: false, isCollapsible: true },
    
    // 8. CRM section (collapsible) - Contacts, Assistants
    { id: "crm", name: "CRM", href: "#", icon: Contact, current: false, isCollapsible: true },
    
    // 9. Settings section (collapsible, top-level)
    { id: "settings", name: "Settings", href: "#", icon: Settings, current: false, isCollapsible: true },
    
    // 10. Master Admin (collapsible section, master admin only)
    { id: "master-admin", name: "Master Admin", href: "#", icon: Shield, current: location === "/master-admin" || location === "/admin/manage-tenants" || location === "/manage-tenants" || location === "/admin/platform-billing" || location === "/admin/platform-roles", isCollapsible: true },
  ];

  const buildMenuItems = (parentId: string) => 
    getMenuItemsByParent(parentId).map(item => ({
      id: item.id,
      name: item.name,
      href: item.href,
      icon: item.icon,
      current: item.currentMatch ? item.currentMatch(location) : false,
    }));

  const mindMenuItems = buildMenuItems("mind");
  const achieveMenuItems = buildMenuItems("achieve");
  const organizeMenuItems = buildMenuItems("organize");
  const aiCoachMenuItems = buildMenuItems("ai-coach");
  const reviewMenuItems = buildMenuItems("review");
  const settingsMenuItems = buildMenuItems("settings");
  const mediaMenuItems = buildMenuItems("media");
  const crmMenuItems = buildMenuItems("crm").filter(
    item => !((item.id === "assistants" || item.id === "coach") && isAssistant),
  );
  const masterAdminMenuItems = buildMenuItems("master-admin").filter(item => {
    if (item.id === "platform-operations" && !hasPermission("tenant.provision", "user.invite", "plan.assign")) return false;
    if (item.id === "platform-billing" && !hasPermission("billing.view", "billing.manage")) return false;
    if (item.id === "platform-roles" && !isPlatformOwner) return false;
    return true;
  });

  // Prepare Files sub-menu items
  // Helper to create sub-menu children
  const createMenuChildren = (menuItems: typeof mindMenuItems, parentId?: string) => {
    return menuItems
      .filter(item => {
        if (hiddenMenuItems.includes(item.id)) return false;
        if (parentId && hiddenMenuItems.includes(parentId)) return false;
        // For assistants: keep all items visible (capability gating is visual only)
        const featureKey = featureKeyMap[item.id];
        if (isAssistant) return true;
        if (!featureKey || planLoading) return true;
        return hasFeature(featureKey as any);
      })
      .map((item) => {
        const Icon = item.icon;
        const assistantAccessible = assistantEnabledIds === null
          ? undefined
          : assistantEnabledIds.has(item.id) ? true : false;
        const assistantDisabled = assistantAccessible === false;
        const assistantEnabled = assistantAccessible === true;
        const inactiveClass = assistantEnabled
          ? "text-emerald-700 dark:text-emerald-500 hover:text-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950"
          : "text-muted-foreground hover:text-foreground hover:bg-muted";
        return (
          <div
            key={item.id}
            className={`flex items-center gap-1${assistantDisabled ? " opacity-40 pointer-events-none select-none" : ""}`}
          >
            <span className="flex-shrink-0 p-1 opacity-60 cursor-grab">
              <GripVertical className="w-4 h-4 text-muted-foreground" />
            </span>
            <Link href={item.href} className="flex-grow min-w-0">
              <Button
                variant={item.current ? "default" : "ghost"}
                className={`w-full justify-start pl-4 ${
                  item.current 
                    ? "bg-primary text-primary-foreground" 
                    : inactiveClass
                }`}
                onClick={() => setSheetOpen(false)}
                data-testid={`link-mobile-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-4 h-4 mr-3" />
                {item.name}
              </Button>
            </Link>
          </div>
        );
      });
  };

  // Prepare sub-menu children for each section
  const mindChildren = createMenuChildren(mindMenuItems, "mind");
  const achieveChildren = createMenuChildren(achieveMenuItems, "achieve");
  const organizeChildren = createMenuChildren(organizeMenuItems, "organize");
  const aiCoachChildren = createMenuChildren(aiCoachMenuItems, "ai-coach");
  const reviewChildren = createMenuChildren(reviewMenuItems, "review");
  const settingsChildren = createMenuChildren(settingsMenuItems, "settings");
  const mediaChildren = createMenuChildren(mediaMenuItems, "media");
  const crmChildren = createMenuChildren(crmMenuItems, "crm");
  const masterAdminChildren = createMenuChildren(masterAdminMenuItems, "master-admin");

  // Build Review section with all children (Analytics, Achievements, and Settings items)
  // Filter settings items by plan access (matching desktop sidebar behavior)
  const allSettingsItems = settingsMenuItems
    .filter(item => {
      if (hiddenMenuItems.includes(item.id)) return false;
      // For assistants: keep all items visible (capability gating is visual only)
      if (isAssistant) return true;
      const featureKey = featureKeyMap[item.id];
      if (!featureKey || planLoading) return true;
      return hasFeature(featureKey as any);
    })
    .map((item) => {
      const Icon = item.icon;
      const assistantAccessible = assistantEnabledIds === null
        ? undefined
        : assistantEnabledIds.has(item.id) ? true : false;
      const assistantDisabled = assistantAccessible === false;
      const assistantEnabled = assistantAccessible === true;
      const inactiveClass = assistantEnabled
        ? "text-emerald-700 dark:text-emerald-500 hover:text-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950"
        : "text-muted-foreground hover:text-foreground hover:bg-muted";
      return (
        <div
          key={item.id}
          className={`flex items-center gap-1${assistantDisabled ? " opacity-40 pointer-events-none select-none" : ""}`}
        >
          <span className="flex-shrink-0 p-1 opacity-60 cursor-grab">
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </span>
          <Link href={item.href} className="flex-grow min-w-0">
            <Button
              variant={item.current ? "default" : "ghost"}
              className={`w-full justify-start pl-4 ${
                item.current 
                  ? "bg-primary text-primary-foreground" 
                  : inactiveClass
              }`}
              onClick={() => setSheetOpen(false)}
              data-testid={`link-mobile-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="w-4 h-4 mr-3" />
              {item.name}
            </Button>
          </Link>
        </div>
      );
    });
  

  // Filter navigation based on plan access, custom menu visibility, and add children to collapsible items
  const filteredNavigation = defaultNavigation
    .filter(item => {
      // Hide items the user has toggled off in Custom Menu settings
      if (hiddenMenuItems.includes(item.id)) return false;

      // Master Admin section is only visible to the platform owner
      if (item.id === "master-admin" && !isMasterAdmin) return false;

      const featureKey = featureKeyMap[item.id];
      // Hide Assistants menu from assistant users (they can't invite other assistants)
      if (item.id === "assistants" && isAssistant) return false;
      // Always show collapsible sections (unless user explicitly hid them)
      if (item.isCollapsible) return true;
      // If no feature key mapping or plan loading, show item by default
      if (!featureKey || planLoading) return true;
      // Check plan access
      return hasFeature(featureKey as any);
    })
    .map(item => {
      // Add children to Mind collapsible item
      if (item.id === "mind") {
        return { ...item, children: mindChildren };
      }
      // Add children to Achieve collapsible item
      if (item.id === "achieve") {
        return { ...item, children: achieveChildren };
      }
      // Add children to Organize collapsible item
      if (item.id === "organize") {
        return { ...item, children: organizeChildren };
      }
      // Add children to AI Coach collapsible item
      if (item.id === "ai-coach") {
        return { ...item, children: aiCoachChildren };
      }
      // Add children to Media collapsible item
      if (item.id === "media") {
        return { ...item, children: mediaChildren };
      }
      // Add children to Review collapsible item
      if (item.id === "review") {
        return { ...item, children: reviewChildren };
      }
      // Add children to Settings collapsible item
      if (item.id === "settings") {
        return { ...item, children: allSettingsItems };
      }
      // Add children to CRM collapsible item
      if (item.id === "crm") {
        return { ...item, children: crmChildren };
      }
      // Add children to Master Admin collapsible item
      if (item.id === "master-admin") {
        return { ...item, children: masterAdminChildren };
      }
      return item;
    });

  const { items: navigation, reorderItems, setItemParent } = useNavigationOrder(filteredNavigation, isAuthenticated);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragEndEvent) => {
    setActiveId(event.active.id as string);
    setNestTargetId(null);
    setDropPosition(null);
  };

  const handleDragOver = (event: any) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      setNestTargetId(null);
      setDropPosition(null);
      return;
    }

    const overItem = navigation.find(item => item.id === over.id);
    if (!overItem || overItem.isCollapsible) {
      setNestTargetId(null);
      setDropPosition('before');
      return;
    }

    const overRect = event.over?.rect;
    const pointerX = (event.active.rect.current.translated?.left || 0) + (event.active.rect.current.translated?.width || 0) / 2;
    
    if (overRect && pointerX > overRect.left + overRect.width * 0.6) {
      setNestTargetId(over.id as string);
      setDropPosition('nest');
    } else {
      setNestTargetId(null);
      const overCenterY = (overRect?.top || 0) + (overRect?.height || 0) / 2;
      const pointerY = (event.active.rect.current.translated?.top || 0) + (event.active.rect.current.translated?.height || 0) / 2;
      setDropPosition(pointerY < overCenterY ? 'before' : 'after');
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const activeItem = navigation.find(item => item.id === active.id);
      const overItem = navigation.find(item => item.id === over.id);
      
      if (activeItem && overItem) {
        if (dropPosition === 'nest' && !overItem.isCollapsible) {
          setItemParent(active.id as string, over.id as string);
        } else {
          const oldIndex = navigation.findIndex((item) => item.id === active.id);
          const newIndex = navigation.findIndex((item) => item.id === over.id);
          
          if (oldIndex !== -1 && newIndex !== -1) {
            const newOrder = arrayMove(navigation, oldIndex, newIndex);
            const movedItem = newOrder.find(item => item.id === active.id);
            if (movedItem && dropPosition !== 'nest') {
              movedItem.parentId = overItem.parentId;
              movedItem.depth = overItem.depth;
            }
            reorderItems(newOrder);
          }
        }
      }
    }
    
    setActiveId(null);
    setNestTargetId(null);
    setDropPosition(null);
  };

  const activeItem = activeId ? navigation.find((item) => item.id === activeId) : null;

  return (
    <div className="lg:hidden fixed top-0 left-0 right-0 z-50 w-full bg-card border-b border-border">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center">
          {resellerConfig?.companyName ? (
            <span className="text-xl font-bold text-foreground" data-testid="text-app-name-mobile">
              {resellerConfig.companyName}
            </span>
          ) : (
            <span className="text-2xl font-bold leading-none" data-testid="text-app-name-mobile">
              <span className="text-foreground">Goal</span><span className="text-orange-500">Via</span>
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <TimeClockButton />
          <MessageBadge />
          <NotificationBell location="mobile" />
          <TourLauncher variant="icon" />
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10"
            onClick={() => setScorecardOpen(true)}
            data-testid="button-scorecard"
            title="Performance Scorecard"
          >
            <Trophy className="w-4 h-4" />
          </Button>
          <Link href="/settings">
            <Button
              variant={location === "/settings" ? "default" : "ghost"}
              size="icon"
              className={`w-8 h-8 ${
                location === "/settings"
                  ? "bg-primary text-primary-foreground" 
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
              data-testid="link-mobile-admin-panel-header"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </Link>
          <Sheet open={quickCreateOpen} onOpenChange={setQuickCreateOpen}>
            <SheetTrigger asChild>
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="button-mobile-quick-create"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-auto max-h-[50vh] rounded-t-xl">
              <SheetHeader className="pb-2">
                <SheetTitle>Quick Create</SheetTitle>
              </SheetHeader>
              <div className="grid grid-cols-2 gap-3 pb-4">
                <Button
                  variant="outline"
                  className="flex flex-col items-center justify-center h-20 gap-2"
                  onClick={() => {
                    setQuickCreateOpen(false);
                    setCreateGoalOpen(true);
                  }}
                  data-testid="button-quick-create-goal"
                >
                  <Target className="w-6 h-6 text-primary" />
                  <span className="text-sm">Goal</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex flex-col items-center justify-center h-20 gap-2"
                  onClick={() => {
                    setQuickCreateOpen(false);
                    setCreateHabitOpen(true);
                  }}
                  data-testid="button-quick-create-habit"
                >
                  <CheckSquare className="w-6 h-6 text-green-500" />
                  <span className="text-sm">Habit</span>
                </Button>
                <Link href="/notepad?quickCreate=note">
                  <Button
                    variant="outline"
                    className="flex flex-col items-center justify-center h-20 gap-2 w-full"
                    onClick={() => setQuickCreateOpen(false)}
                    data-testid="button-quick-create-note"
                  >
                    <StickyNote className="w-6 h-6 text-yellow-500" />
                    <span className="text-sm">Note</span>
                  </Button>
                </Link>
                <Link href="/calendar?quickCreate=event">
                  <Button
                    variant="outline"
                    className="flex flex-col items-center justify-center h-20 gap-2 w-full"
                    onClick={() => setQuickCreateOpen(false)}
                    data-testid="button-quick-create-event"
                  >
                    <CalendarPlus className="w-6 h-6 text-blue-500" />
                    <span className="text-sm">Event</span>
                  </Button>
                </Link>
                <Link href="/tasks?quickCreate=task">
                  <Button
                    variant="outline"
                    className="flex flex-col items-center justify-center h-20 gap-2 w-full"
                    onClick={() => setQuickCreateOpen(false)}
                    data-testid="button-quick-create-task"
                  >
                    <ListTodo className="w-6 h-6 text-purple-500" />
                    <span className="text-sm">Task</span>
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="button-mobile-menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[240px] sm:w-[280px] p-0">
              <div className="flex flex-col h-full">
                {/* Mobile User Profile */}
                <div className="p-4 border-b">
                  <div className="flex items-center space-x-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={(user as any)?.profileImageUrl || undefined} />
                      <AvatarFallback>
                        {user?.firstName?.[0]}{user?.lastName?.[0] || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </div>
                  {tenant && (
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{tenant.name}</span>
                      <div className="flex space-x-1">
                        <Badge variant="outline" className="text-xs">
                          {(tenant as any).planType?.toUpperCase() || 'FREE'}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Mobile Navigation Links with Drag and Drop */}
                <div className="flex-1 p-4 overflow-y-auto">
                  <nav className="space-y-2">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDragEnd={handleDragEnd}
                      modifiers={[restrictToVerticalAxis]}
                    >
                      <SortableContext
                        items={navigation.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {navigation.map((item) => (
                          <SortableNavItem 
                            key={item.id} 
                            item={item} 
                            onNavigate={() => setSheetOpen(false)}
                            isOpen={
                              item.id === "mind" ? mindOpen : 
                              item.id === "achieve" ? achieveOpen : 
                              item.id === "organize" ? organizeOpen : 
                              item.id === "ai-coach" ? aiCoachOpen : 
                              item.id === "media" ? mediaOpen :
                              item.id === "review" ? reviewOpen : 
                              item.id === "settings" ? settingsOpen :
                              item.id === "crm" ? crmOpen :
                              undefined
                            }
                            onToggle={
                              item.id === "mind" ? handleMindToggle : 
                              item.id === "achieve" ? handleAchieveToggle : 
                              item.id === "organize" ? handleOrganizeToggle : 
                              item.id === "ai-coach" ? handleAiCoachToggle : 
                              item.id === "media" ? handleMediaToggle :
                              item.id === "review" ? handleReviewToggle : 
                              item.id === "settings" ? handleSettingsToggle :
                              item.id === "crm" ? handleCrmToggle :
                              undefined
                            }
                            isNestTarget={nestTargetId === item.id}
                          />
                        ))}
                      </SortableContext>
                      <DragOverlay>
                        {activeItem ? (
                          <div className="bg-card border border-border rounded-md shadow-lg">
                            <Button
                              variant="ghost"
                              className="w-64 justify-start cursor-grabbing"
                            >
                              <activeItem.icon className="w-4 h-4 mr-3" />
                              {activeItem.name}
                            </Button>
                          </div>
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  </nav>

                  <CalendarSidebarPanel
                    isAuthenticated={isAuthenticated}
                    instance="mobile"
                  />

                  {/* Categories - Collapsible */}
                  <div className="mt-4">
                    <Collapsible open={categoriesOpen} onOpenChange={setCategoriesOpen}>
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          className="w-full justify-between text-muted-foreground hover:text-foreground hover:bg-muted"
                          data-testid="button-mobile-categories-toggle"
                        >
                          <span className="flex items-center">
                            <Tag className="w-4 h-4 mr-3" />
                            Categories
                          </span>
                          <span className="ml-auto">
                            {categoriesOpen ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </span>
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-1 mt-1">
                        {customCategories.length > 0 ? (
                          customCategories.map((category) => (
                            <Button
                              key={category.id}
                              variant="ghost"
                              className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-muted pl-11"
                              data-testid={`link-mobile-category-${category.name?.toLowerCase().replace(/\s+/g, '-') ?? ''}`}
                            >
                              <div 
                                className="w-3 h-3 rounded-full mr-3" 
                                style={{ backgroundColor: category.color }}
                              ></div>
                              <span className="flex-1 text-left">{category.name}</span>
                              {categoryStats[category.name] && (
                                <Badge variant="secondary" className="ml-auto text-xs">
                                  {categoryStats[category.name]}
                                </Badge>
                              )}
                            </Button>
                          ))
                        ) : (
                          <div className="pl-11 py-2 text-xs text-muted-foreground">
                            No custom categories yet
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>

                </div>
                
                {/* Mobile Logout */}
                <div className="p-4 border-t">
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                    onClick={() => { clearUserScopedStorage(user?.id); window.location.href = "/api/logout"; }}
                    data-testid="button-mobile-logout"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign Out
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      
      {/* Quick Create Modals */}
      <CreateGoalModal open={createGoalOpen} onOpenChange={setCreateGoalOpen} />
      <CreateHabitModal open={createHabitOpen} onOpenChange={setCreateHabitOpen} />
      <ScorecardDialog open={scorecardOpen} onOpenChange={setScorecardOpen} />
    </div>
  );
}
